import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Loader2, Bot, MessageCircle, Clock, User as UserIcon, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import type { User } from '@/types';

interface UserFollowupProps {
  user: User;
  onUpdate?: () => void;
}

interface UserFollowupSettings {
  enabled: boolean;
  followupTime: string;
  priority: 'normal' | 'high' | 'low';
  customMessage?: string;
  lastFollowupDate?: string;
  totalFollowups?: number;
  successfulFollowups?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

const followupTimes = [
  '00:00', '01:00', '02:00', '03:00', '04:00', '05:00', '06:00', '07:00',
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00',
  '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00', '23:00'
];

export function UserFollowup({ user, onUpdate }: UserFollowupProps) {
  const [settings, setSettings] = useState<UserFollowupSettings>({
    enabled: false,
    followupTime: '21:00',
    priority: 'normal'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [globalSettings, setGlobalSettings] = useState<{ enabled: boolean } | null>(null);
  const { t, dir } = useLanguage();
  const isRTL = dir === 'rtl';

  useEffect(() => {
    fetchUserFollowupSettings();
    fetchGlobalSettings();
  }, [user.id]);

  const fetchGlobalSettings = async () => {
    try {
      const globalDoc = await getDoc(doc(db, 'settings', 'aiFollowup'));
      if (globalDoc.exists()) {
        setGlobalSettings(globalDoc.data() as { enabled: boolean });
      }
    } catch (error) {
      console.error('Error fetching global AI follow-up settings:', error);
    }
  };

  const fetchUserFollowupSettings = async () => {
    try {
      setIsLoading(true);
      const userFollowupDoc = await getDoc(doc(db, 'userFollowupSettings', user.id));
      
      if (userFollowupDoc.exists()) {
        const data = userFollowupDoc.data() as UserFollowupSettings;
        setSettings({
          enabled: data.enabled || false,
          followupTime: data.followupTime || '21:00',
          priority: data.priority || 'normal',
          customMessage: data.customMessage,
          lastFollowupDate: data.lastFollowupDate,
          totalFollowups: data.totalFollowups || 0,
          successfulFollowups: data.successfulFollowups || 0,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt
        });
      } else {
        // Set default settings for new user
        setSettings({
          enabled: false,
          followupTime: '21:00',
          priority: 'normal',
          totalFollowups: 0,
          successfulFollowups: 0
        });
      }
    } catch (error) {
      console.error('Error fetching user follow-up settings:', error);
      toast.error(t('dashboard.admin.users.followup.fetchError') || 'Failed to fetch follow-up settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleFollowup = async (enabled: boolean) => {
    try {
      setIsSaving(true);
      
      // Create clean settings object without undefined values
      const updatedSettings: any = {
        enabled,
        followupTime: settings.followupTime,
        priority: settings.priority,
        updatedAt: new Date()
      };

      // If enabling for the first time, set createdAt
      if (enabled && !settings.createdAt) {
        updatedSettings.createdAt = new Date();
      } else if (settings.createdAt) {
        updatedSettings.createdAt = settings.createdAt;
      }

      // Only include optional fields if they have values
      if (settings.customMessage !== undefined && settings.customMessage !== '') {
        updatedSettings.customMessage = settings.customMessage;
      }
      if (settings.lastFollowupDate !== undefined) {
        updatedSettings.lastFollowupDate = settings.lastFollowupDate;
      }
      if (settings.totalFollowups !== undefined) {
        updatedSettings.totalFollowups = settings.totalFollowups;
      }
      if (settings.successfulFollowups !== undefined) {
        updatedSettings.successfulFollowups = settings.successfulFollowups;
      }

      await setDoc(doc(db, 'userFollowupSettings', user.id), updatedSettings, { merge: true });
      
      setSettings({ ...settings, ...updatedSettings });
      
      toast.success(
        enabled 
          ? (t('dashboard.admin.users.followup.enabledSuccess') || 'AI follow-up enabled for user')
          : (t('dashboard.admin.users.followup.disabledSuccess') || 'AI follow-up disabled for user')
      );
      
      onUpdate?.();
    } catch (error) {
      console.error('Error updating follow-up settings:', error);
      toast.error(t('dashboard.admin.users.followup.updateError') || 'Failed to update follow-up settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setIsSaving(true);
      
      // Filter out undefined values to avoid Firestore errors
      const updatedSettings: any = {
        enabled: settings.enabled,
        followupTime: settings.followupTime,
        priority: settings.priority,
        updatedAt: new Date()
      };

      // Only include optional fields if they have values
      if (settings.customMessage !== undefined && settings.customMessage !== '') {
        updatedSettings.customMessage = settings.customMessage;
      }
      if (settings.lastFollowupDate !== undefined) {
        updatedSettings.lastFollowupDate = settings.lastFollowupDate;
      }
      if (settings.totalFollowups !== undefined) {
        updatedSettings.totalFollowups = settings.totalFollowups;
      }
      if (settings.successfulFollowups !== undefined) {
        updatedSettings.successfulFollowups = settings.successfulFollowups;
      }
      if (settings.createdAt !== undefined) {
        updatedSettings.createdAt = settings.createdAt;
      }

      await updateDoc(doc(db, 'userFollowupSettings', user.id), updatedSettings);
      
      setSettings({ ...settings, updatedAt: new Date() });
      
      toast.success(t('dashboard.admin.users.followup.settingsSaved') || 'Follow-up settings saved successfully');
      
      onUpdate?.();
    } catch (error) {
      console.error('Error saving follow-up settings:', error);
      toast.error(t('dashboard.admin.users.followup.saveError') || 'Failed to save follow-up settings');
    } finally {
      setIsSaving(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'low': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'high': return t('dashboard.admin.users.followup.priority.high') || 'High';
      case 'low': return t('dashboard.admin.users.followup.priority.low') || 'Low';
      default: return t('dashboard.admin.users.followup.priority.normal') || 'Normal';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const userName = isRTL 
    ? `${user.firstNameAr || user.firstNameEn} ${user.lastNameAr || user.lastNameEn}`
    : `${user.firstNameEn || user.firstNameAr} ${user.lastNameEn || user.lastNameAr}`;

  return (
    <div className="space-y-6" dir={dir}>
      {/* Global Status Info */}
      {!globalSettings?.enabled && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <AlertCircle className="h-5 w-5 text-blue-600" />
              <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                <p className="text-sm font-medium text-blue-800">
                  {t('dashboard.admin.users.followup.globalDisabled') || 'AI Follow-up System is Globally Disabled'}
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  {t('dashboard.admin.users.followup.individualOverride') || 'You can still enable AI follow-up for individual users. Individual settings override global settings.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Info Header */}
      <Card>
        <CardHeader>
          <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
              <UserIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              <CardTitle className="text-xl">
                {t('dashboard.admin.users.followup.title') || 'AI Follow-up Settings'}
              </CardTitle>
              <CardDescription>
                {t('dashboard.admin.users.followup.userDescription', { userName }) || `Configure AI follow-up settings for ${userName}`}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Main Settings */}
      <Card>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Bot className="h-5 w-5 text-blue-600" />
            {t('dashboard.admin.users.followup.settings') || 'Follow-up Settings'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <MessageCircle className="h-5 w-5 text-blue-600" />
              <div className={`space-y-1 ${isRTL ? 'text-right' : ''}`}>
                <Label htmlFor="followup-enabled" className="text-sm font-medium cursor-pointer">
                  {t('dashboard.admin.users.followup.enableForUser') || 'Enable AI Follow-up for this User'}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t('dashboard.admin.users.followup.enableDesc') || 'When enabled, this user will receive personalized daily habit follow-up messages'}
                </p>
              </div>
            </div>
            <Switch
              id="followup-enabled"
              checked={settings.enabled}
              onCheckedChange={handleToggleFollowup}
              disabled={isSaving}
            />
          </div>

          <Separator />

          {/* Time Settings */}
          <div className="space-y-4">
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Clock className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">
                {t('dashboard.admin.users.followup.followupTime') || 'Daily Follow-up Time'}
              </Label>
            </div>
            <Select
              value={settings.followupTime}
              onValueChange={(value) => setSettings({ ...settings, followupTime: value })}
              disabled={isSaving}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {followupTimes.map((time) => (
                  <SelectItem key={time} value={time}>
                    {time}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t('dashboard.admin.users.followup.timeDesc') || 'Time when daily habit follow-up messages will be sent to this user'}
            </p>
          </div>

          {/* Priority Settings */}
          <div className="space-y-4">
            <Label className="text-sm font-medium">
              {t('dashboard.admin.users.followup.priority.label') || 'Follow-up Priority'}
            </Label>
            <Select
              value={settings.priority}
              onValueChange={(value: 'normal' | 'high' | 'low') => setSettings({ ...settings, priority: value })}
              disabled={isSaving}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="high">
                  {t('dashboard.admin.users.followup.priority.high') || 'High Priority'}
                </SelectItem>
                <SelectItem value="normal">
                  {t('dashboard.admin.users.followup.priority.normal') || 'Normal Priority'}
                </SelectItem>
                <SelectItem value="low">
                  {t('dashboard.admin.users.followup.priority.low') || 'Low Priority'}
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t('dashboard.admin.users.followup.priorityDesc') || 'High priority users will be processed first during daily follow-ups'}
            </p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <Button
              onClick={handleSaveSettings}
              disabled={isSaving}
              className="min-w-[120px]"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('dashboard.admin.users.followup.saving') || 'Saving...'}
                </>
              ) : (
                t('dashboard.admin.users.followup.saveSettings') || 'Save Settings'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      {settings.enabled && (settings.totalFollowups || 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {t('dashboard.admin.users.followup.statistics') || 'Follow-up Statistics'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`text-center p-4 bg-blue-50 rounded-lg ${isRTL ? 'text-right' : 'text-left'}`}>
                <div className="text-2xl font-bold text-blue-600">
                  {settings.totalFollowups || 0}
                </div>
                <div className="text-sm text-blue-800">
                  {t('dashboard.admin.users.followup.stats.total') || 'Total Follow-ups'}
                </div>
              </div>
              <div className={`text-center p-4 bg-green-50 rounded-lg ${isRTL ? 'text-right' : 'text-left'}`}>
                <div className="text-2xl font-bold text-green-600">
                  {settings.successfulFollowups || 0}
                </div>
                <div className="text-sm text-green-800">
                  {t('dashboard.admin.users.followup.stats.successful') || 'Successful'}
                </div>
              </div>
              <div className={`text-center p-4 bg-purple-50 rounded-lg ${isRTL ? 'text-right' : 'text-left'}`}>
                <div className="text-2xl font-bold text-purple-600">
                  {settings.totalFollowups ? Math.round(((settings.successfulFollowups || 0) / settings.totalFollowups) * 100) : 0}%
                </div>
                <div className="text-sm text-purple-800">
                  {t('dashboard.admin.users.followup.stats.successRate') || 'Success Rate'}
                </div>
              </div>
            </div>
            
            {settings.lastFollowupDate && (
              <div className="mt-4 pt-4 border-t">
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className="text-sm text-muted-foreground">
                    {t('dashboard.admin.users.followup.stats.lastFollowup') || 'Last Follow-up:'}
                  </span>
                  <Badge variant="outline" className={getPriorityColor(settings.priority)}>
                    {settings.lastFollowupDate}
                  </Badge>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Current Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {t('dashboard.admin.users.followup.currentStatus') || 'Current Status'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`w-3 h-3 rounded-full ${settings.enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
              <span className="text-sm font-medium">
                {settings.enabled 
                  ? (t('dashboard.admin.users.followup.status.active') || 'Active')
                  : (t('dashboard.admin.users.followup.status.inactive') || 'Inactive')
                }
              </span>
            </div>
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Badge variant="outline" className={getPriorityColor(settings.priority)}>
                {getPriorityLabel(settings.priority)}
              </Badge>
              {settings.enabled && (
                <Badge variant="outline">
                  {settings.followupTime}
                </Badge>
              )}
            </div>
          </div>
          
          {settings.enabled && (
            <div className="mt-4 p-3 bg-green-50 rounded-lg">
              <p className="text-sm text-green-800">
                {t('dashboard.admin.users.followup.activeMessage', { 
                  userName, 
                  time: settings.followupTime 
                }) || `${userName} will receive daily AI follow-up messages at ${settings.followupTime}`}
                {!globalSettings?.enabled && (
                  <span className="block mt-1 text-xs font-medium">
                    {t('dashboard.admin.users.followup.overrideActive') || '(Individual override - works even when global system is disabled)'}
                  </span>
                )}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
